import { styled } from "~/ui/style/stitches.config";
import { sendErrorToast } from "~/utils/handleToasts";

type AppleSignInResponse = {
  authorization: { id_token: string; code: string; state?: string };
  user?: { email?: string; name?: { firstName?: string; lastName?: string } };
};

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: {
          clientId: string;
          scope: string;
          redirectURI: string;
          usePopup: boolean;
        }) => void;
        signIn: () => Promise<AppleSignInResponse>;
      };
    };
  }
}

// Shared with Google's renderButton so both social buttons are the same width
export const SOCIAL_BUTTON_WIDTH = 200;

export const AppleSigninButton = ({
  onSignIn,
}: {
  onSignIn: (idToken: string, name: string | null) => Promise<void>;
}) => {
  const handleClick = async () => {
    // Apple's script failed to load (e.g. blocked by an ad blocker)
    if (!window.AppleID) {
      sendErrorToast(1187);
      return;
    }
    let response: AppleSignInResponse;
    try {
      window.AppleID.auth.init({
        clientId: import.meta.env.VITE_APPLE_SERVICES_ID as string,
        scope: "name email",
        redirectURI: `${window.location.origin}/callback/oauth2`,
        usePopup: true,
      });
      response = await window.AppleID.auth.signIn();
    } catch (error: any) {
      // User closed or cancelled the Apple popup
      if (
        error?.error === "popup_closed_by_user" ||
        error?.error === "user_cancelled_authorize"
      ) {
        return;
      }
      console.error("Sign in with Apple failed:", error);
      sendErrorToast(1186);
      return;
    }
    // Apple only includes the name on the user's first authorization
    const fullName = [
      response.user?.name?.firstName,
      response.user?.name?.lastName,
    ]
      .filter(Boolean)
      .join(" ");
    await onSignIn(response.authorization.id_token, fullName || null);
  };

  return (
    <StyledAppleButton type="button" onClick={handleClick}>
      <img src="/images/apple-logo-large.svg" alt="" aria-hidden="true" />
      Continue with Apple
    </StyledAppleButton>
  );
};

const StyledAppleButton = styled("button", {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "16px auto 0",
  height: "40px",
  width: `${SOCIAL_BUTTON_WIDTH}px`,
  padding: "0 12px 0 38px",
  borderRadius: "20px",
  border: "1px solid #dadce0",
  backgroundColor: "#fff",
  color: "#000",
  fontFamily: "WorkSans",
  fontSize: "14px",
  fontWeight: 400,
  letterSpacing: "0",
  cursor: "pointer",
  // Apple's logo file includes its own padding and must span the full button height
  "& img": {
    position: "absolute",
    left: "4px",
    top: 0,
    height: "100%",
  },
  "&:hover": {
    backgroundColor: "#f8faff",
  },
});
